// LT-311 — Billing sub-route. Combines Subscription + Credits behind a
// single tab toggle. `/subscription` redirects to /settings/billing;
// `/credits` redirects to /settings/billing?tab=credits.
// LT-314 — marks the onboarding checklist "Review billing" task complete
// on first mount (persists in localStorage).
import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SubscriptionPage from '../SubscriptionPage';
import CreditsPage from '../CreditsPage';
import { markBillingViewed } from '../../components/OnboardingChecklist/useChecklistSignals';

type BillingView = 'subscription' | 'credits';

const SettingsBillingTab: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab');
  const view: BillingView = rawTab === 'credits' ? 'credits' : 'subscription';

  useEffect(() => {
    markBillingViewed();
  }, []);

  const setView = (next: BillingView) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'subscription') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', next);
    }
    setParams(nextParams, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div
        role="tablist"
        aria-label="Billing view"
        className="inline-flex rounded-ds-lg border border-white/[0.08] bg-surface-1 p-1"
      >
        <button
          role="tab"
          aria-selected={view === 'subscription'}
          onClick={() => setView('subscription')}
          className={`px-3 py-1 text-sm rounded-ds-md transition-colors ${
            view === 'subscription'
              ? 'bg-accent-tint text-accent-text border border-accent-border'
              : 'text-slate-400 hover:text-slate-200 border border-transparent'
          }`}
        >
          Subscription
        </button>
        <button
          role="tab"
          aria-selected={view === 'credits'}
          onClick={() => setView('credits')}
          className={`px-3 py-1 text-sm rounded-ds-md transition-colors ${
            view === 'credits'
              ? 'bg-accent-tint text-accent-text border border-accent-border'
              : 'text-slate-400 hover:text-slate-200 border border-transparent'
          }`}
        >
          Credits
        </button>
      </div>

      {view === 'subscription' ? <SubscriptionPage /> : <CreditsPage />}
    </div>
  );
};

export default SettingsBillingTab;
